import { useEffect, useState, useMemo, useCallback } from "react";
import { isEmpty } from "lodash";
import { formApi } from "api/form";
import * as api from "api/api";
import { useApp } from "contexts/AppContext";
import { useContentfulContext } from "hooks/useContentful";
import { useAsyncError } from "hooks/useAsyncError";
import {
  Stack,
  Typography,
  Container,
  Paper,
  Button,
  Radio,
  TextField,
  CircularProgress,
  Alert,
  Grid,
  Checkbox,
  Divider,
} from "@newulife/common";
import { useNavigate } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { devToolsState } from "state";
import { mockApiCall } from "helpers/mockApiCall";
import axios from "axios";
import config from "config";
import {
  createInputIdFromName,
  createInputPatternFromName,
} from "helpers/form";


// Mapping from field identifier to rep property name and miscField name
const FIELD_MAPPING = {
  citizenship: {
    repProperty: "MALDistributor",
    miscField: "MALDistributor",
    inputName: "citizenship",
    isRadio: true,
  },
  companyregistrationnumber: {
    repProperty: "CompanyRegistrationNumber",
    miscField: "CompanyRegistrationNumber",
    inputName: "companyregistrationnumber",
    isRadio: false,
  },
  industriescode: {
    repProperty: "IndustriesCode",
    miscField: "IndustriesCode",
    inputName: "industriescode",
    isRadio: false,
  },
  passportnumber: {
    repProperty: "PassportNumber",
    miscField: "PassportNumber",
    inputName: "passportnumber",
    isRadio: false,
  },
  identitycardnumber: {
    repProperty: "IdentityCardNumber",
    miscField: "IdentityCardNumber",
    inputName: "identitycardnumber",
    isRadio: false,
  },
  tin: {
    repProperty: "TIN",
    miscField: "TIN",
    inputName: "tin",
    isRadio: false,
  },
};

const MalaysiaVerification = () => {
    const data = useContentfulContext({
        entryPoint: "fields",
      })
  const { title, description, inputModelArray, text1: remindMeLaterText } = data;
  const { text2: noBusinessEntityText } = inputModelArray?.[0]?.fields || {};

  const {
    state: {
      rep: { RepDID, country, RomanizedCompany },
      rep,
    },
  } = useApp();

  const navigate = useNavigate();

  const [form, setForm] = useState({
    MALDistributor: undefined,
    CompanyRegistrationNumber: "",
    IndustriesCode: "",
    PassportNumber: "",
    IdentityCardNumber: "",
    TIN: "",
    NoBusinessEntity: false,
  });
  const [saveApiError, setSaveApiError] = useState(0);
  const [saveApiIsLoading, setSaveApiIsLoading] = useState(false);
  const [saveApiErrorCode, setSaveApiErrorCode] = useState(false);
  const devToolsSettings = useRecoilValue(devToolsState);
  const throwError = useAsyncError();

  // Fetch miscFields from API when component loads
  useEffect(() => {
    const fetchMiscFields = async () => {
      try {
        const miscFields = await formApi.getMiscFields();
        if (miscFields && Object.keys(miscFields).length > 0) {
          // If TIN or Identity Card Number already exists, treat as Malaysian (true) even if MALDistributor was false
          const hasTinOrIdNumber =
            !isEmpty(miscFields.TIN) || !isEmpty(miscFields.IdentityCardNumber);
          const initialForm = {
            MALDistributor: hasTinOrIdNumber
              ? true
              : typeof miscFields.MALDistributor === "boolean"
                ? miscFields.MALDistributor
                : true,
            CompanyRegistrationNumber: miscFields.CompanyRegistrationNumber || "",
            IndustriesCode: miscFields.IndustriesCode || "",
            PassportNumber: miscFields.PassportNumber || "",
            IdentityCardNumber: miscFields.IdentityCardNumber || "",
            TIN: miscFields.TIN || "",
            NoBusinessEntity: false, // UI-only: affects validation only, not persisted or submitted
          };
          setForm(initialForm);
        }
      } catch (error) {
        console.error("Error fetching miscFields:", error);
      }
    };

    if (RepDID) {
      fetchMiscFields();
    }
  }, [RepDID]);

  // Check if company name exists
  const hasCompanyName = !isEmpty(RomanizedCompany);

  // Get citizenship value - only true if explicitly set to true
  const citizenshipValue = form.MALDistributor;
  const isMalaysian = citizenshipValue === true;
  const isCitizenshipSelected =
    citizenshipValue !== null && citizenshipValue !== undefined;

  // Determine which fields should be visible
  const shouldShowField = useCallback(
    (fieldKey) => {
      // Always show citizenship field
      if (fieldKey === "citizenship") return true;

      // Show company and industry fields only if company name exists
      if (
        hasCompanyName &&
        (fieldKey === "companyregistrationnumber" ||
          fieldKey === "industriescode")
      ) {
        return true;
      }

      // Only show TIN when user has selected Malaysian citizenship
      if (fieldKey === "tin" && isCitizenshipSelected && isMalaysian) return true;

      // Only show passport/identity card fields if citizenship is selected
      if (!isCitizenshipSelected) return false;

      // If Malaysian, show identity card number
      if (isMalaysian && fieldKey === "identitycardnumber") return true;

      // If Non-Malaysian, show passport number
      if (!isMalaysian && fieldKey === "passportnumber") return true;

      return false;
    },
    [hasCompanyName, isMalaysian, isCitizenshipSelected]
  );

  // Get all fields in FIELD_MAPPING order with their inputModels
  // Filter to only show fields that should be visible based on account type and citizenship
  const fieldsWithModels = useMemo(() => {
    if (!inputModelArray?.[0]?.fields?.inputModelArray || !Array.isArray(inputModelArray?.[0]?.fields?.inputModelArray)) {
      return [];
    }

    // Create a map of fieldIdentifier/name to inputModel for quick lookup
    const inputModelMap = new Map();
    inputModelArray?.[0]?.fields?.inputModelArray?.forEach((inputModel) => {
      const fieldIdentifier = inputModel?.fields?.fieldIdentifier?.toLowerCase();
      const fieldName =
        inputModel?.fields?.name?.toLowerCase().replace(/\s+/g, "") || "";

      // Store by fieldIdentifier if available
      if (fieldIdentifier) {
        inputModelMap.set(fieldIdentifier, inputModel);
      }
      // Also store by fieldName for fallback matching
      if (fieldName) {
        inputModelMap.set(fieldName, inputModel);
      }
    });

    return Object.keys(FIELD_MAPPING)
      .filter((fieldKey) => shouldShowField(fieldKey))
      .map((fieldKey) => {
        const mapping = FIELD_MAPPING[fieldKey];
        // Find the matching inputModel for this fieldKey
        let inputModel = inputModelMap.get(fieldKey);

        // If not found by exact match, try to find by matching fieldIdentifier or name
        if (!inputModel) {
          inputModel = inputModelArray.find((model) => {
            const fieldIdentifier = model?.fields?.fieldIdentifier?.toLowerCase();
            const fieldName =
              model?.fields?.name?.toLowerCase().replace(/\s+/g, "") || "";
            return (
              fieldIdentifier === fieldKey ||
              fieldName === fieldKey ||
              fieldName.includes(fieldKey) ||
              fieldKey.includes(fieldName)
            );
          });
        }

        return {
          fieldKey,
          mapping,
          inputModel,
        };
      })
      .filter((item) => item.inputModel) // Only include fields that have inputModels
      .sort((a, b) => {
        // Custom sort order: citizenship first, then TIN, then passport/identity card
        // Citizenship always first
        if (a.fieldKey === "citizenship") return -1;
        if (b.fieldKey === "citizenship") return 1;

        // For individual accounts, TIN should come before passport/identity card, then ID name
        if (a.fieldKey === "tin") return -1;
        if (b.fieldKey === "tin") return 1;
        if (
          a.fieldKey === "passportnumber" ||
          a.fieldKey === "identitycardnumber"
        )
          return -1;
        if (
          b.fieldKey === "passportnumber" ||
          b.fieldKey === "identitycardnumber"
        )
          return 1;

        // For non-individual, maintain FIELD_MAPPING order
        const order = [
          "citizenship",
          "companyregistrationnumber",
          "industriescode",
          "tin",
          "passportnumber",
          "identitycardnumber",
        ];
        return order.indexOf(a.fieldKey) - order.indexOf(b.fieldKey);
      });
  }, [inputModelArray, shouldShowField]);

  const onFormChange = (e, fieldKey) => {
    const mapping = FIELD_MAPPING[fieldKey];
    if (!mapping) return;

    if (mapping.isRadio) {
      const value = e.target.value === "true";
      setForm({ ...form, [mapping.repProperty]: value });
    } else {
      const { value } = e.target;
      setForm({ ...form, [mapping.repProperty]: value });
    }
  };

  const onSave = async () => {
    setSaveApiIsLoading(true);
    //devTools Values for Error
    const devToolsError =
      devToolsSettings.disableVerificationRedirect &&
      devToolsSettings.verificationSubmissionResult === "error";

    // submit the data
    try {
      if (devToolsError) {
        await mockApiCall("post-autoresponder-details");
      } else {
        // Blank the ID field they don't need (only on save, so presaved values stay in UI if they toggle for fun)
        const formToSave = { ...form };
        if (form.MALDistributor === true) {
          formToSave.PassportNumber = null;
        } else if (form.MALDistributor === false) {
          formToSave.IdentityCardNumber = null;
          formToSave.TIN = null;
        }
        await formApi.saveMalaysiaMiscFields({ form: formToSave });

        // Once save is successful, get login URL and redirect
        const response = await api.getDashSsoUrl();
        if (response.url) {
          setSaveApiError(0);
          setSaveApiIsLoading(false);
          if (!devToolsSettings.disableVerificationRedirect) {
            window.location.assign(response.url);
          }
        } else {
          throwError(`Problem authenticating with Dash: ${response.code}`);
        }
      }
    } catch (error) {
      setSaveApiError(saveApiError + 1);
      setSaveApiErrorCode(error.response?.data?.id);
      setSaveApiIsLoading(false);
      console.error(
        "Error during the save validation:",
        error.response ? error.response.data.message : error.response?.data
      );
    }
  };

  const onRemindMeLater = async () => {
    setSaveApiIsLoading(true);
    try {
      const response = await api.getDashSsoUrl();
      if (response.url) {
        if (!devToolsSettings.disableVerificationRedirect) {
          window.location.assign(response.url);
        }
      } else {
        throwError(`Problem authenticating with Dash: ${response.code}`);
        setSaveApiIsLoading(false);
      }
    } catch (error) {
      setSaveApiIsLoading(false);
      console.error("Error redirecting to Dash:", error);
      throwError(error?.response?.data?.message || "Unable to redirect to Dash.");
    }
  };

  // When user has business name and checks "no business entity", business ID and industries code are optional
  const skipBusinessFieldsValidation =
    hasCompanyName && form.NoBusinessEntity === true;

  // Validate form - check if all visible required fields are filled
  const isFormValid = useMemo(() => {
    let isValid = true;
    fieldsWithModels.forEach(({ fieldKey, mapping }) => {
      if (
        skipBusinessFieldsValidation &&
        (fieldKey === "companyregistrationnumber" || fieldKey === "industriescode")
      ) {
        return;
      }
      if (mapping.isRadio) {
        if (
          form[mapping.repProperty] === null ||
          form[mapping.repProperty] === undefined
        ) {
          isValid = false;
        }
      } else {
        if (isEmpty(form[mapping.repProperty])) {
          isValid = false;
        }
      }
    });
    return isValid;
  }, [form, fieldsWithModels, skipBusinessFieldsValidation]);

  return (
    <Stack sx={{ width: "100%" }}>
      <Container>
        <Paper
          elevation={3}
          sx={{
            width: "100%",
            maxWidth: "700px",
            mx: "auto",
            position: "relative",
          }}
        >
          <Stack
            display="grid"
            p={{
              xs: 2,
              sm: 3,
            }}
            gap={4}
          >
            <Stack
              display="grid"
              gap={2}
              justifyContent="center"
              justifyItems="center"
              justifySelf="center"
              textAlign="center"
              maxWidth="500px"
            >
              <Typography
                variant="header1"
                fontWeight="400"
                fontSize="24px"
                lineHeight="28px"
                color="neutralCool.dark"
              >
                {title}
              </Typography>
              <Typography variant="body3">{description}</Typography>
            </Stack>
            <div className="form-max flex-20 column">
              <Stack gap={2}>
                {fieldsWithModels.map(({ fieldKey, mapping, inputModel }) => {
                  const showDividerAbove =
                    hasCompanyName && fieldKey === "companyregistrationnumber";
                  return (
                    <Stack key={fieldKey} gap={2}>
                      {showDividerAbove && (
                        <>
                          <Divider sx={{ width: "100%", my: 1 }} />
                          {/* If we ever want to show the business name, do it this way 
                          <Typography variant="body2" fontWeight="600">
                            {repPropertyToLabel.RomanizedCompany}:{" "}
                            <Typography component="span" fontWeight="400">
                              {rep.RomanizedCompany}
                            </Typography>
                          </Typography> */}
                        </>
                      )}
                      {mapping.isRadio ? (
                        <>
                          <Typography variant="body2" fontWeight="600">
                            {inputModel?.fields?.name || fieldKey}
                          </Typography>
                          <Stack gap={2}>
                            <Stack
                              display="grid"
                              gridAutoFlow="column"
                              gap={1}
                              justifyContent="start"
                              alignItems="center"
                              sx={{ cursor: "pointer" }}
                              onClick={() =>
                                setForm({ ...form, [mapping.repProperty]: true })
                              }
                            >
                              <Radio
                                checked={form[mapping.repProperty] === true}
                                value="true"
                                name={`citizenship_${fieldKey}`}
                                onChange={(e) => onFormChange(e, fieldKey)}
                              />
                              <Typography>
                                {inputModel?.fields?.selectOptions?.[0]?.fields?.name}
                              </Typography>
                            </Stack>
                            <Stack
                              display="grid"
                              gridAutoFlow="column"
                              gap={1}
                              justifyContent="start"
                              alignItems="center"
                              sx={{ cursor: "pointer" }}
                              onClick={() =>
                                setForm({ ...form, [mapping.repProperty]: false })
                              }
                            >
                              <Radio
                                checked={form[mapping.repProperty] === false}
                                value="false"
                                name={`citizenship_${fieldKey}`}
                                onChange={(e) => onFormChange(e, fieldKey)}
                              />
                              <Typography>
                                {inputModel?.fields?.selectOptions?.[1]?.fields?.name}
                              </Typography>
                            </Stack>
                          </Stack>
                          {inputModel?.fields?.description && (
                            <Typography variant="body3" color="neutralCool.main">
                              {inputModel.fields.description}
                            </Typography>
                          )}
                        </>
                      ) : (
                        <Stack gap={1}>
                          <TextField
                            variant="filled"
                            id={createInputIdFromName(mapping.inputName)}
                            name={mapping.inputName}
                            pattern={createInputPatternFromName(
                              mapping.inputName,
                              country
                            )}
                            value={form[mapping.repProperty] || ""}
                            onChange={(e) => onFormChange(e, fieldKey)}
                            isErrored={false}
                            label={inputModel?.fields?.name || fieldKey}
                          />
                          {inputModel?.fields?.description && (
                            <Typography variant="body3" color="neutralCool.main">
                              {inputModel.fields.description}
                            </Typography>
                          )}
                        </Stack>
                      )}
                    </Stack>
                  );
                })}
                {hasCompanyName && (
                  <Stack
                    direction="row"
                    alignItems="center"
                    gap={1}
                    sx={{ mt: 1, cursor: "pointer" }}
                    onClick={() =>
                      setForm((prev) => ({
                        ...prev,
                        NoBusinessEntity: !prev.NoBusinessEntity,
                      }))
                    }
                  >
                    <Checkbox
                      checked={form.NoBusinessEntity}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          NoBusinessEntity: e.target.checked,
                        }))
                      }
                      onClick={(e) => e.stopPropagation()}
                    />
                    <Typography variant="body2">{noBusinessEntityText}</Typography>
                  </Stack>
                )}
              </Stack>
              <Stack direction="column" gap={2} sx={{ mt: 4 }} flexWrap="wrap">
                <Button
                  size="large"
                  disabled={!isFormValid || saveApiIsLoading}
                  onClick={onSave}
                >
                  {inputModelArray?.[1]?.fields?.title}
                </Button>
                <Button
                  variant="text"
                  disabled={saveApiIsLoading}
                  onClick={onRemindMeLater}
                  sx={{
                    color: "primary.main",
                    "&:hover": {
                      textDecoration: "underline",
                      backgroundColor: "transparent",
                      color: "secondary.main",
                      textDecoration: "underline",
                    },
                  }}
                >
                  {remindMeLaterText}
                </Button>
              </Stack>
            </div>
            {(saveApiError > 0 && !saveApiIsLoading) && (
              <Alert
                severity="error"
                variant="standard"
                className="alert"
              >
                <Grid container gap={1} direction="column">
                  <Typography align="left" variant="body2">
                    {inputModelArray?.[2]?.fields?.title}
                  </Typography>
                  {saveApiError >= 3 && (
                    <Typography align="left" variant="subhead1">
                      {inputModelArray?.[2]?.fields?.richText}
                    </Typography>
                  )}
                  {saveApiErrorCode && (
                    <Typography align="left" variant="label2">
                      {inputModelArray?.[2]?.fields?.item1} {saveApiErrorCode}
                    </Typography>
                  )}
                </Grid>
              </Alert>
            )}
          </Stack>
        </Paper>
      </Container>
      {saveApiIsLoading && (
        <Stack
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            bgcolor: 'rgba(255, 255, 255, 0.5)',
          }}
        >
          <CircularProgress
            size="lg"
            sx={{
              m: 'auto',
            }}
          />
        </Stack>
      )}
    </Stack>
  );
};

export default MalaysiaVerification;

